import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, ShieldOff, Loader2, Check, Lock } from "lucide-react";

export default function ParentDashboardPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/parent/dashboard`, { params: { token } });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to load dashboard");
    }
  }, [token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const act = async (action) => {
    const msg = action === "revoke"
      ? "Pause your teen's access to Omni? They will be logged out and unable to use the app until you restore access."
      : "Restore your teen's access to Omni?";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/parent/${action}`, { token });
      toast.success(res.message);
      await fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="card-omni p-10 max-w-md text-center" data-testid="parent-dashboard-error">
          <h1 className="font-heading text-xl font-medium mb-2">Invalid link</h1>
          <p className="text-sm text-muted-foreground">{String(error)}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const { child, modules } = data;
  const active = child.status === "active";

  return (
    <div className="min-h-screen bg-background p-6" data-testid="parent-dashboard">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-center gap-3 my-8">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
          <span className="font-heading text-xl font-medium">Omni — Parent Dashboard</span>
        </div>

        <div className="card-omni p-8 mb-6" data-testid="parent-child-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-heading text-2xl font-medium">{child.username}</h1>
              <p className="text-xs text-muted-foreground mt-1">
                Plan: <span className="capitalize font-medium text-foreground">{child.tier}</span>
                {child.teen_safe && <span className="ml-2 text-green-600 font-medium">Teen-safe mode on</span>}
              </p>
            </div>
            <span
              data-testid="parent-consent-status"
              className={`px-4 py-1.5 rounded-full text-xs font-semibold ${active ? "bg-green-100 text-green-700" : "bg-destructive/10 text-destructive"}`}
            >
              {active ? "Access Active" : "Access Paused"}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
            As parent/guardian of a 13–15 year old, you control this account. Teen-safe content settings are always on:
            mature themes are filtered, the AI never gives medical or legal advice, and crisis mentions route to
            teen-appropriate help resources.
          </p>
          <div className="mt-5">
            {active ? (
              <Button data-testid="parent-revoke-btn" onClick={() => act("revoke")} disabled={busy} variant="outline" className="rounded-full border-destructive/50 text-destructive hover:bg-destructive/10 gap-2">
                <ShieldOff className="w-4 h-4" /> Revoke Consent — Pause Access
              </Button>
            ) : (
              <Button data-testid="parent-approve-btn" onClick={() => act("approve")} disabled={busy} className="btn-omni bg-primary text-primary-foreground rounded-full gap-2">
                <ShieldCheck className="w-4 h-4" /> Restore Access
              </Button>
            )}
          </div>
        </div>

        <div className="card-omni p-8" data-testid="parent-modules-card">
          <h2 className="font-heading text-lg font-medium mb-4">Modules your teen can use</h2>
          <div className="space-y-3">
            {modules.map(m => (
              <div key={m.id} data-testid={`parent-module-${m.id}`} className="flex items-start gap-3 p-3 rounded-xl bg-muted/30 border border-border/30">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${m.accessible ? "bg-green-100" : "bg-muted"}`}>
                  {m.accessible ? <Check className="w-4 h-4 text-green-600" /> : <Lock className="w-4 h-4 text-muted-foreground" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {m.name}
                    {!m.accessible && <span className="ml-2 text-[10px] text-muted-foreground uppercase tracking-wide">not on current plan</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">{m.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-6 mb-10 max-w-md mx-auto">
          Questions or data requests: <a href="mailto:privacy@omnimodules.com" className="text-primary underline">privacy@omnimodules.com</a> ·{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link> · Keep your dashboard link private — it is your access key.
        </p>
      </div>
    </div>
  );
}
